// CustomMarkerOverlay.ts

// Factory functions that create the classes after Google Maps is loaded
export function createMarkerOverlayClasses() {
	// Base class for custom marker overlays
	abstract class BaseMarkerOverlay extends google.maps.OverlayView {
		protected div: HTMLDivElement | null = null;

		constructor(
			protected position: google.maps.LatLng,
			protected imagePath: string,
			protected pointType: string,
			protected locationName?: string
		) {
			super();
		}

		// Abstract method that subclasses must implement to provide their HTML content
		protected abstract getHtmlContent(): string;

		onAdd() {
			const div = document.createElement('div');
			div.style.position = 'absolute';
			div.style.transform = 'translate(-50%, -100%)'; // Center horizontally, position above point
			div.innerHTML = this.getHtmlContent();

			this.div = div;
			const panes = this.getPanes();
			panes?.overlayMouseTarget.appendChild(div);
		}

		draw() {
			if (!this.div) return;

			const overlayProjection = this.getProjection();
			const position = overlayProjection.fromLatLngToDivPixel(this.position);

			if (position) {
				this.div.style.left = position.x + 'px';
				this.div.style.top = position.y + 'px';
			}
		}

		onRemove() {
			if (this.div) {
				this.div.parentNode?.removeChild(this.div);
				this.div = null;
			}
		}
	}

	// Regular marker overlay for pickup/dropoff
	class CustomMarkerOverlay extends BaseMarkerOverlay {
		protected getHtmlContent(): string {
			return `
				<div style="
					background: white;
					border-radius: 8px;
					box-shadow: 0 4px 12px rgba(0,0,0,0.15);
					padding: 4px 4px;
					max-width: 104px;
					font-family: Poppins, sans-serif;
					position: relative;
				">
					<div style="display: flex; align-items: center; margin-bottom: 0px;">
						<div style="width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; margin-right: 8px; flex-shrink: 0;">
							<img src="${this.imagePath}" alt="${this.pointType}" style="width: 36px; height: 36px;" />
						</div>
						<div style="display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 0;">
							<h3 style="margin: 0; color: black; font-size: 12px; font-weight: 400;">${this.pointType}</h3>
							${this.locationName ? `<span style="margin: 0; color: #afafaf; font-size: 12px; font-weight: 400; line-height: 1.2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%;">${this.locationName}</span>` : ''}
						</div>
					</div>

					<!-- Pointer arrow -->
					<div style="
						position: absolute;
						bottom: -8px;
						left: 50%;
						transform: translateX(-50%);
						width: 0;
						height: 0;
						border-left: 8px solid transparent;
						border-right: 8px solid transparent;
						border-top: 8px solid white;
						filter: drop-shadow(0 2px 2px rgba(0,0,0,0.1));
					"></div>
				</div>
			`;
		}
	}

	// Completed marker overlay with different styling
	class CompletedMarkerOverlay extends BaseMarkerOverlay {
		protected getHtmlContent(): string {
			return `
				<div style="
					background: transparent;
					font-family: Poppins, sans-serif;
					position: relative;
				">
					<div style="display: flex; align-items: center; margin-bottom: 0px;">
						<div style="display: flex; align-items: center; justify-content: center;">
							<img src="${this.imagePath}" alt="${this.pointType}" style="width: 20px; height: 20px;" />
						</div>
					</div>


				</div>
			`;
		}
	}

	// Vehicle marker overlay with rotation support
	class VehicleMarkerOverlay extends google.maps.OverlayView {
		protected div: HTMLDivElement | null = null;
		private position: google.maps.LatLng;
		private imagePath: string;
		private rotation: number;

		constructor(position: google.maps.LatLng, imagePath: string, rotation: number) {
			super();
			this.position = position;
			this.imagePath = imagePath;
			this.rotation = rotation;
		}

		onAdd() {
			const div = document.createElement('div');
			div.style.position = 'absolute';
			div.style.width = '48px';
			div.style.height = '48px';
			div.style.transform = `translate(-50%, -50%) rotate(${this.rotation}deg)`;
			div.style.transformOrigin = 'center center';

			const img = document.createElement('img');
			img.src = this.imagePath;
			img.style.width = '100%';
			img.style.height = '100%';
			img.style.display = 'block';

			div.appendChild(img);
			this.div = div;

			const panes = this.getPanes();
			// Use markerLayer pane - above polylines but below overlayMouseTarget (pickup/dropoff markers)
			panes?.markerLayer.appendChild(div);
		}

		draw() {
			if (!this.div) return;

			const overlayProjection = this.getProjection();
			const position = overlayProjection.fromLatLngToDivPixel(this.position);

			if (position) {
				this.div.style.left = position.x + 'px';
				this.div.style.top = position.y + 'px';
			}
		}

		onRemove() {
			if (this.div) {
				this.div.parentNode?.removeChild(this.div);
				this.div = null;
			}
		}

		// Method to update position and rotation
		updatePositionAndRotation(position: google.maps.LatLng, rotation: number) {
			this.position = position;
			this.rotation = rotation;
			if (this.div) {
				this.div.style.transform = `translate(-50%, -50%) rotate(${rotation}deg)`;
			}
			this.draw();
		}
	}

	return {
		CustomMarkerOverlay,
		CompletedMarkerOverlay,
		VehicleMarkerOverlay
	};
}
